import { Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/actionReports";
import { IconChevronDown } from "../../../../common/icons/IconChevronDown/IconChevronDown";
import "./GroupLeaderFilter.scss";

/**
 * Disabled-dropdown fallback rendered when a child `GroupLeaderFilter`
 * throws during render (e.g. malformed `groupLeaderTree`). Per the design,
 * the filter is disabled rather than hidden so the user can still see that
 * something went wrong.
 */
const DisabledFallback = () => {
  const { t } = useTranslation(["actionReports"]);
  return (
    <div className="GroupLeaderFilter GroupLeaderFilter--disabled">
      <button
        type="button"
        className="GroupLeaderFilter__trigger"
        disabled
        title={t("filter.groupLeader.errorTooltip", defaultContent.filter.groupLeader.errorTooltip)}
      >
        <span className="GroupLeaderFilter__label">
          {t("filter.groupLeader.label", defaultContent.filter.groupLeader.label)}
        </span>
        <span className="GroupLeaderFilter__value">
          {t("filter.groupLeader.errorTooltip", defaultContent.filter.groupLeader.errorTooltip)}
        </span>
        <IconChevronDown color="black" />
      </button>
    </div>
  );
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class GroupLeaderFilterErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    console.error("[GroupLeaderFilter] malformed tree:", error);
  }

  render(): ReactNode {
    if (this.state.hasError) return <DisabledFallback />;
    return this.props.children;
  }
}
